import {
  Card,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import useApiEp from "../hooks/useApiEp";

const MessageVolumeCard = ({
  title = "Monthly Message Volume",
  chipLabel = "Current Year",
  height = 300,
  colors,
}) => {
  const { data, isLoading, isError, error } = useApiEp(
    "/user/weekly-message-volume"
  );

  // Month abbreviations
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Normalize API data for chart
  const chartData =
    data?.data?.map((item) => ({
      month: monthNames[item.month - 1], // Convert number to month name
      sent: item.count,
    })) || [];

  // Get year from API response or use current year as fallback
  const displayYear = data?.year || new Date().getFullYear();

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        background: `
              linear-gradient(135deg,
                rgba(255, 255, 255, 0.95) 0%,
                rgba(248, 250, 252, 0.9) 100%
              )
            `,
        backdropFilter: "blur(20px)",
        boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 12px 28px rgba(79, 70, 229, 0.15)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Corner accent */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 120,
          height: 120,
          background:
            "radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          position: "relative",
          zIndex: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.textPrimary,
            letterSpacing: "0.5px",
            "&::after": {
              content: '""',
              display: "block",
              width: 48,
              height: 3,
              background: `linear-gradient(90deg, ${colors.primary}, transparent)`,
              borderRadius: 3,
              mt: 1,
            },
          }}
        >
          {title}
        </Typography>

        <Chip
          label={displayYear}
          size="small"
          sx={{
            background: "rgba(79, 70, 229, 0.08)",
            color: colors.textPrimary,
            border: "1px solid rgba(79, 70, 229, 0.2)",
            fontWeight: 500,
            backdropFilter: "blur(4px)",
          }}
        />
      </Box>

      {/* Content */}
      {isLoading && (
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}

      {isError && (
        <Alert severity="error">
          {error?.message || "Failed to load message volume"}
        </Alert>
      )}

      {!isLoading && !isError && (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={colors.primary}
                  stopOpacity={0.8}
                />
                <stop
                  offset="100%"
                  stopColor={colors.primary}
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(99,102,241,0.08)"
              vertical={false}
            />

            <XAxis
              dataKey="month"
              tick={{ fill: colors.textSecondary, fontSize: "0.75rem" }}
              axisLine={{ stroke: "rgba(99,102,241,0.15)" }}
              tickLine={false}
            />

            <YAxis
              tick={{ fill: colors.textSecondary, fontSize: "0.75rem" }}
              axisLine={{ stroke: "rgba(99,102,241,0.15)" }}
              tickLine={false}
            />

            <Tooltip
              contentStyle={{
                background: "rgba(255,255,255,0.95)",
                borderRadius: 12,
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              }}
            />

            <Line
              type="monotone"
              dataKey="sent"
              stroke={colors.primary}
              strokeWidth={3}
              dot={{
                r: 4,
                fill: "#fff",
                stroke: colors.primary,
                strokeWidth: 2,
              }}
              activeDot={{
                r: 7,
                fill: colors.primary,
                stroke: colors.secondary,
                strokeWidth: 2,
              }}
            />

            <Area
              type="monotone"
              dataKey="sent"
              fill="url(#lineGradient)"
              fillOpacity={0.25}
              strokeWidth={0}
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Bottom glow */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 60,
          background:
            "linear-gradient(to top, rgba(79,70,229,0.03), transparent)",
          pointerEvents: "none",
        }}
      />
    </Card>
  );
};

export default MessageVolumeCard;
